import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIndianRupeeSign,
  faWallet,
  faRotateRight,
  faEnvelope,
  faIdBadge,
  faHashtag,
  faPhone,
} from '@fortawesome/free-solid-svg-icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  addUserBalance,
  getUserBalanceById,
  getUserPaymentsForUser,
  getUsers,
} from '../../../lib/api';
import './admin.css';

const ITEMS_PER_PAGE = 25;

const formatMoney = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return String(v ?? '—');
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 5 }).format(n);
};

const typeBadgeClass = (type) => {
  const t = (type || '').toLowerCase();
  if (t === 'credit') return 'admin-type-badge admin-type-badge--credit';
  if (t === 'debit') return 'admin-type-badge admin-type-badge--debit';
  if (t === 'refund') return 'admin-type-badge admin-type-badge--refund';
  return 'admin-type-badge admin-type-badge--other';
};

const AddBalance = () => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [balance, setBalance] = useState('0');
  const [payments, setPayments] = useState([]);
  const [totals, setTotals] = useState({ credit: '0', debit: '0', refund: '0' });
  const [paymentPage, setPaymentPage] = useState(1);
  const [paymentTotalPages, setPaymentTotalPages] = useState(1);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selectedUser = useMemo(
    () => users.find((u) => String(u.id) === String(selectedUserId)),
    [users, selectedUserId]
  );

  const chartData = useMemo(() => {
    const map = new Map();
    for (const t of payments) {
      if (!t?.payment_date) continue;
      const d = new Date(t.payment_date).toISOString().split('T')[0];
      const cur = map.get(d) || { date: d, credit: 0, debit: 0, refund: 0 };
      const amt = parseFloat(t.amount) || 0;
      const typ = (t.transaction_type || '').toLowerCase();
      if (typ === 'credit') cur.credit += amt;
      else if (typ === 'debit') cur.debit += amt;
      else if (typ === 'refund') cur.refund += amt;
      map.set(d, cur);
    }
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [payments]);

  useEffect(() => {
    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const data = await getUsers(1, 100);
        setUsers(data?.data || []);
      } catch (e) {
        setError(e?.message || 'Failed to load users');
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);

  const syncUserList = useCallback(async () => {
    try {
      const data = await getUsers(1, 100);
      setUsers(data?.data || []);
    } catch {
      /* keep existing list */
    }
  }, []);

  const loadLedger = useCallback(async (userId, page) => {
    if (!userId) return;
    setLoadingDetail(true);
    setError('');
    try {
      const [bal, pay] = await Promise.all([
        getUserBalanceById(userId),
        getUserPaymentsForUser(userId, page, ITEMS_PER_PAGE),
      ]);
      const liveBal = bal?.balance;
      setBalance(String(liveBal ?? '0'));
      setPayments(pay?.data || []);
      setTotals(pay?.totals || { credit: '0', debit: '0', refund: '0' });
      setPaymentTotalPages(pay?.pagination?.total_pages || 1);
      setPaymentPage(pay?.pagination?.current_page || page);
      // Keep `/user/users` row in sync with live wallet when fields match API
      setUsers((prev) =>
        prev.map((u) =>
          String(u.id) !== String(userId)
            ? u
            : {
                ...u,
                balance: liveBal ?? u.balance,
                remaining_balance:
                  bal?.remaining_balance != null ? bal.remaining_balance : u.remaining_balance ?? liveBal ?? u.balance,
              }
        )
      );
    } catch (e) {
      setError(e?.message || 'Failed to load ledger');
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedUserId) {
      setPayments([]);
      setTotals({ credit: '0', debit: '0', refund: '0' });
      setBalance('0');
      setPaymentPage(1);
      setPaymentTotalPages(1);
      setMessage('');
      return;
    }
    loadLedger(selectedUserId, paymentPage);
  }, [selectedUserId, paymentPage, loadLedger]);

  const onAdd = async () => {
    setMessage('');
    setError('');
    if (!selectedUserId || !amount) {
      setError('Please select a user and enter an amount.');
      return;
    }
    setSubmitting(true);
    try {
      await addUserBalance(selectedUserId, amount);
      setMessage(`Added ${formatMoney(amount)} successfully.`);
      setAmount('');
      await loadLedger(selectedUserId, paymentPage);
      await syncUserList();
    } catch (e) {
      setError(e?.message || 'Failed to add balance');
    } finally {
      setSubmitting(false);
    }
  };

  const onRefreshLedger = async () => {
    if (!selectedUserId) return;
    await loadLedger(selectedUserId, paymentPage);
    await syncUserList();
  };

  return (
    <div className="admin-page">
      <header className="admin-page__hero">
        <div className="admin-page__title-row">
          <div className="admin-page__icon" aria-hidden>
            <FontAwesomeIcon icon={faIndianRupeeSign} />
          </div>
          <div>
            <span className="admin-page__eyebrow">Administration</span>
            <h1 className="admin-page__title">Add balance</h1>
            <p className="admin-page__subtitle">
              Credit a user wallet, inspect running credit / debit / refund totals, view a trend chart, and browse
              paginated payment history for the selected account.
            </p>
          </div>
        </div>
      </header>

      <section className="admin-panel">
        <div className="admin-panel__head">
          <h2>Credit wallet</h2>
          <p>{loadingUsers ? 'Loading user list…' : `${users.length} user(s) available`}</p>
        </div>
        <div className="admin-panel__body">
          {error && (
            <div className="admin-feedback admin-feedback--error" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}
          <div className="admin-grid">
            <div className="admin-field">
              <span className="admin-label">User</span>
              <select
                className="admin-select"
                value={selectedUserId}
                onChange={(e) => {
                  setSelectedUserId(e.target.value);
                  setPaymentPage(1);
                  setMessage('');
                }}
                disabled={loadingUsers}
              >
                <option value="">Select user</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.id} — {u.name || u.email || 'Unnamed'}
                    {u.phone_number || u.phoneNumber
                      ? ` — ${u.phone_number || u.phoneNumber}`
                      : ''} — Bal {formatMoney(u.balance)} / Rem{' '}
                    {formatMoney(u.remaining_balance ?? u.balance)}
                  </option>
                ))}
              </select>
            </div>
            <div className="admin-field">
              <span className="admin-label">Amount (INR)</span>
              <input
                className="admin-input"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 500"
              />
            </div>
            <div className="admin-field">
              <span className="admin-label">Balance (from user list API)</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, minHeight: 46 }}>
                <span className="admin-pill admin-pill--balance" title="balance on /user/users">
                  Bal {selectedUserId ? formatMoney(selectedUser?.balance) : '—'}
                </span>
                <span className="admin-pill admin-pill--refund" title="remaining_balance on /user/users">
                  Rem {selectedUserId ? formatMoney(selectedUser?.remaining_balance ?? selectedUser?.balance) : '—'}
                </span>
              </div>
            </div>
            <div className="admin-field">
              <span className="admin-label">Live balance (POST /user/balance)</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 46 }}>
                <span className="admin-pill admin-pill--balance">
                  <FontAwesomeIcon icon={faWallet} />
                  {loadingDetail && selectedUserId ? '…' : formatMoney(balance)}
                </span>
              </div>
            </div>
          </div>

          <div className="admin-actions">
            <button
              type="button"
              className="admin-btn admin-btn--primary"
              onClick={onAdd}
              disabled={submitting || !selectedUserId}
            >
              {submitting ? 'Applying…' : 'Add balance'}
            </button>
            <button
              type="button"
              className="admin-btn admin-btn--ghost"
              onClick={onRefreshLedger}
              disabled={!selectedUserId || loadingDetail}
              title="Reload ledger"
            >
              <FontAwesomeIcon icon={faRotateRight} /> Refresh
            </button>
            {message && <span className="admin-feedback admin-feedback--success">{message}</span>}
          </div>
        </div>
      </section>

      {selectedUserId ? (
        <>
          <section className="admin-panel">
            <div className="admin-panel__head">
              <h2>Account overview</h2>
              <p>Identifiers and wallet summary for the selected user.</p>
            </div>
            <div className="admin-panel__body">
              <div className="admin-user-strip">
                <div className="admin-user-strip__item">
                  <span className="admin-user-strip__k">User ID</span>
                  <span className="admin-user-strip__v">{selectedUserId}</span>
                </div>
                <div className="admin-user-strip__item">
                  <span className="admin-user-strip__k">Name</span>
                  <span className="admin-user-strip__v">
                    <FontAwesomeIcon icon={faIdBadge} style={{ marginRight: 6, opacity: 0.7 }} />
                    {selectedUser?.name || '—'}
                  </span>
                </div>
                <div className="admin-user-strip__item">
                  <span className="admin-user-strip__k">Email</span>
                  <span className="admin-user-strip__v">
                    <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 6, opacity: 0.7 }} />
                    {selectedUser?.email || '—'}
                  </span>
                </div>
                <div className="admin-user-strip__item">
                  <span className="admin-user-strip__k">Phone number</span>
                  <span className="admin-user-strip__v">
                    <FontAwesomeIcon icon={faPhone} style={{ marginRight: 6, opacity: 0.7 }} />
                    {selectedUser?.phone_number || selectedUser?.phoneNumber || '—'}
                  </span>
                </div>
                <div className="admin-user-strip__item">
                  <span className="admin-user-strip__k">Balance (list)</span>
                  <span className="admin-user-strip__v">{formatMoney(selectedUser?.balance)}</span>
                </div>
                <div className="admin-user-strip__item">
                  <span className="admin-user-strip__k">Remaining (list)</span>
                  <span className="admin-user-strip__v">
                    {formatMoney(selectedUser?.remaining_balance ?? selectedUser?.balance)}
                  </span>
                </div>
                <div className="admin-user-strip__item">
                  <span className="admin-user-strip__k">Live balance</span>
                  <span className="admin-user-strip__v">{formatMoney(balance)}</span>
                </div>
                <div className="admin-user-strip__item">
                  <span className="admin-user-strip__k">WABA ID</span>
                  <span className="admin-user-strip__v">{selectedUser?.waba_id ?? '—'}</span>
                </div>
                <div className="admin-user-strip__item">
                  <span className="admin-user-strip__k">Phone number ID</span>
                  <span className="admin-user-strip__v">
                    <FontAwesomeIcon icon={faHashtag} style={{ marginRight: 6, opacity: 0.7 }} />
                    {selectedUser?.phone_number_id ?? '—'}
                  </span>
                </div>
                <div className="admin-user-strip__item">
                  <span className="admin-user-strip__k">Status</span>
                  <span className="admin-user-strip__v">
                    {selectedUser?.status === 1 ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="admin-totals">
                <span className="admin-pill admin-pill--credit">Total credit: {formatMoney(totals.credit)}</span>
                <span className="admin-pill admin-pill--debit">Total debit: {formatMoney(totals.debit)}</span>
                <span className="admin-pill admin-pill--refund">Total refund: {formatMoney(totals.refund)}</span>
                <span className="admin-pill admin-pill--balance">List balance: {formatMoney(selectedUser?.balance)}</span>
                <span className="admin-pill admin-pill--balance">Live balance: {formatMoney(balance)}</span>
              </div>

              <h3 className="admin-section-title">Activity by day</h3>
              <p style={{ margin: '0 0 8px', fontSize: '0.85rem', color: 'var(--muted)' }}>
                Credit, debit, and refund amounts aggregated per calendar day (current page of transactions).
              </p>
              <div className="admin-chart-wrap">
                {loadingDetail ? (
                  <div className="admin-empty" style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                    Loading chart…
                  </div>
                ) : chartData.length === 0 ? (
                  <div className="admin-empty" style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                    No payment rows on this page to chart.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis dataKey="date" tick={{ fill: 'var(--muted)', fontSize: 11 }} />
                      <YAxis tick={{ fill: 'var(--muted)', fontSize: 11 }} />
                      <Tooltip
                        formatter={(value) => formatMoney(value)}
                        labelStyle={{ color: 'var(--text)' }}
                        contentStyle={{
                          background: 'var(--card)',
                          border: '1px solid var(--border)',
                          borderRadius: 8,
                        }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="credit" name="Credit" stroke="#10b981" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="debit" name="Debit" stroke="#6366f1" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="refund" name="Refund" stroke="#f59e0b" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </section>

          <section className="admin-panel">
            <div className="admin-panel__head">
              <h2>Payment ledger</h2>
              <p>
                {loadingDetail
                  ? 'Loading…'
                  : `${payments.length} row(s) on page ${paymentPage} of ${paymentTotalPages}`}
              </p>
            </div>
            <div className="admin-panel__body" style={{ padding: 0 }}>
              <div className="admin-scroll">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Payment ID</th>
                      <th>Method</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Campaign ID</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingDetail ? (
                      <tr>
                        <td colSpan="6" className="admin-empty">
                          Loading transactions…
                        </td>
                      </tr>
                    ) : (
                      <>
                        {payments.map((p) => (
                          <tr key={p.payment_id}>
                            <td>{p.payment_id}</td>
                            <td>{p.payment_method || '—'}</td>
                            <td>
                              <span className={typeBadgeClass(p.transaction_type)}>{p.transaction_type || '—'}</span>
                            </td>
                            <td>{formatMoney(p.amount)}</td>
                            <td>{p.campaign_id != null ? p.campaign_id : '—'}</td>
                            <td>{p.payment_date ? new Date(p.payment_date).toLocaleString() : '—'}</td>
                          </tr>
                        ))}
                        {!payments.length && (
                          <tr>
                            <td colSpan="6" className="admin-empty">
                              No transactions on this page.
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="admin-pagination">
                <span className="admin-pagination__meta">
                  Page {paymentPage} of {paymentTotalPages}
                </span>
                <div className="admin-pagination__controls">
                  <button
                    type="button"
                    className="admin-btn admin-btn--ghost"
                    onClick={() => setPaymentPage((p) => Math.max(1, p - 1))}
                    disabled={paymentPage <= 1 || loadingDetail}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn--ghost"
                    onClick={() => setPaymentPage((p) => Math.min(paymentTotalPages, p + 1))}
                    disabled={paymentPage >= paymentTotalPages || loadingDetail}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
};

export default AddBalance;
